import { useEffect, useState } from "react";
import { Trophy, Crown } from "lucide-react";
import { api } from "@/lib/api";

const formatFJD = (n) => {
    if (!n || n <= 0) return "FJ$0";
    if (n >= 1_000_000) return `FJ$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M+`;
    if (n >= 1_000) return `FJ$${Math.round(n / 1_000)}k+`;
    return `FJ$${n}`;
};

const RANK_TONES = [
    "bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-500/40",  // gold
    "bg-slate-400/15 text-slate-700 dark:text-slate-200 border-slate-400/40",  // silver
    "bg-orange-700/15 text-orange-700 dark:text-orange-300 border-orange-700/40",  // bronze
];

export default function ReferralLeaderboard() {
    const [items, setItems] = useState([]);

    useEffect(() => {
        let mounted = true;
        api.get("/public/referral-leaderboard?limit=10")
            .then(({ data }) => mounted && setItems(data?.items || []))
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, []);

    if (!items.length) return null;

    return (
        <section
            data-testid="referral-leaderboard"
            className="px-4 sm:px-6 lg:px-8 py-12 bg-card border-y border-border"
        >
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-2 mb-2">
                    <Crown className="h-4 w-4 text-coral" />
                    <span className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-app font-semibold text-coral">
                        Referral Leaderboard · Last 90 days
                    </span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold mb-2 text-foreground">
                    The people sending Fiji's best deals our way.
                </h2>
                <p className="font-app text-muted-foreground max-w-2xl mb-6">
                    Top referrers earn a share of every Joint Venture they introduce that closes. Names anonymized — first name only — but earnings are real.
                </p>
                <div className="space-y-2" data-testid="leaderboard-list">
                    {items.map((it, idx) => {
                        const tone = RANK_TONES[idx] || "bg-secondary text-foreground border-border";
                        return (
                            <div
                                key={`${it.rank}-${it.display_name}`}
                                data-testid={`leaderboard-row-${it.rank}`}
                                className="flex items-center gap-4 p-4 rounded-lg bg-background border border-border hover:border-coral/40 transition-colors"
                            >
                                <div className={`h-10 w-10 rounded-full grid place-items-center font-display font-bold text-base border ${tone}`}>
                                    {it.rank}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-semibold text-foreground truncate">{it.display_name}</div>
                                    <div className="text-xs font-app text-muted-foreground">
                                        {it.deal_count} deal{it.deal_count === 1 ? "" : "s"} closed
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display font-bold tabular-nums text-fiji-green text-lg">
                                        {formatFJD(it.earnings_fjd_floored)}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">earned</div>
                                </div>
                                {idx < 3 && <Trophy className="h-4 w-4 text-coral flex-shrink-0" />}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
